/* ==========================================================================
   Spicy South - Diet Mode (Veg / Non-Veg) on the LIVE Savor theme.
   Single module, no dependencies. Ported from the Rebuild Phase-1 system.

   Mechanic:
   - localStorage 'ss_diet_mode' = 'veg' | 'nonveg' (default 'nonveg');
     keys are identical to Phase 1 so visitor state survives theme moves.
   - data-diet-mode attribute is set pre-paint on <html> by an inline head
     script in layout/theme.liquid; mirrored onto <body> here.
   - All visual swaps are CSS driven off that attribute (assets/diet-mode.css).
     This file flips state, lazily hydrates veg card overlays, strips the
     internal "[veg]" alt-text marker from what screen readers hear, and
     fires guarded analytics. The first-visit chooser has its own inline
     controller in snippets/diet-chooser.liquid (self-contained by design).
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'ss_diet_mode';
  var VEG = 'veg';
  var NONVEG = 'nonveg';

  function store(k, v) {
    try { localStorage.setItem(k, v); } catch (e) { /* private mode: session-only */ }
  }
  function read(k) {
    try { return localStorage.getItem(k); } catch (e) { return null; }
  }
  function getMode() {
    return read(KEY) === VEG ? VEG : NONVEG;
  }

  /* ------------------------------------------------------------------
     Veg card-image hydration.
     Card overlays render with data-diet-src (no src), so non-veg
     visitors never pay their bytes. First veg intent hydrates them;
     the overlay only crossfades in once loaded ('ds-ready'), so a slow
     image can never blank a card.
     ------------------------------------------------------------------ */
  var hydrated = false;

  function hydrateImg(img) {
    if (img.getAttribute('src')) return;
    var src = img.getAttribute('data-diet-src');
    if (!src) return;
    img.addEventListener('load', function () {
      var wrap = img.closest('.ds-card-veg, .ds-hero-veg');
      if (wrap) wrap.classList.add('ds-ready');
    }, { once: true });
    /* hero overlay art direction: hydrate <source>/srcset before src so
       the browser fetches only the variant for the current viewport */
    var pic = img.parentElement;
    if (pic && pic.tagName === 'PICTURE') {
      var srcs = pic.querySelectorAll('source[data-diet-srcset]');
      for (var si = 0; si < srcs.length; si++) {
        srcs[si].setAttribute('srcset', srcs[si].getAttribute('data-diet-srcset'));
      }
    }
    var ss = img.getAttribute('data-diet-srcset');
    if (ss) img.setAttribute('srcset', ss);
    /* safety net: if the load event never fires (cache quirks, decode
       stalls), un-gate the crossfade anyway - a decoded-but-hidden image
       is worse than a slightly early fade */
    setTimeout(function () {
      var wrap = img.closest('.ds-card-veg, .ds-hero-veg');
      if (wrap && img.naturalWidth > 0) wrap.classList.add('ds-ready');
    }, 3000);
    img.setAttribute('src', src);
    if (img.complete && img.naturalWidth > 0) {
      var wrap = img.closest('.ds-card-veg, .ds-hero-veg');
      if (wrap) wrap.classList.add('ds-ready');
    }
  }

  function hydrate() {
    hydrated = true;
    var vegNow = getMode() === VEG;
    var imgs = document.querySelectorAll('img[data-diet-src]');
    for (var i = 0; i < imgs.length; i++) {
      /* the hero variant is heavy - fetch it only for veg mode; a later
         veg flip re-runs hydrate() and picks it up then */
      if (!vegNow && imgs[i].closest('.ds-hero-veg')) continue;
      hydrateImg(imgs[i]);
    }
  }

  /* Strip the internal "[veg]" marker from alt text and aria-labels so
     assistive tech never hears it. Markup-safe: display only. */
  function cleanAlts() {
    var els = document.querySelectorAll('img[alt*="[veg]" i], [aria-label*="[veg]" i]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.hasAttribute('alt')) {
        el.setAttribute('alt', el.getAttribute('alt').replace(/\s*\[veg\]\s*/gi, ' ').trim());
      }
      if (el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', el.getAttribute('aria-label').replace(/\s*\[veg\]\s*/gi, ' ').trim());
      }
    }
  }

  function track(eventName, mode) {
    try {
      if (typeof window.dataLayer !== 'undefined' && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: eventName, diet_mode: mode });
      }
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', eventName, { mode: mode });
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  function apply(mode) {
    document.documentElement.setAttribute('data-diet-mode', mode);
    if (document.body) document.body.setAttribute('data-diet-mode', mode);
    var toggles = document.querySelectorAll('[data-diet-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-checked', mode === VEG ? 'true' : 'false');
    }
    if (mode === VEG && typeof ensureVegChip === 'function') ensureVegChip();
  }

  function setMode(mode, eventName) {
    store(KEY, mode);
    apply(mode);
    if (mode === VEG || !hydrated) hydrate();
    if (eventName) track(eventName, mode);
  }

  /* Expose for the chooser's inline controller (keeps one source of truth) */
  window.SSDiet = { setMode: setMode, getMode: getMode, hydrate: hydrate };

  /* ------------------------------------------------------------------
     Phase 3 - motion refinements.
     a) toggle change pop (adds a one-beat class; CSS does the spring)
     b) mode-switch confirmation chip (FSSAI mark + leaf/ember beat)
     c) PDP veg chip over galleries that actually have a veg set
     d) Lottie bridge: every animation beat is a named SLOT. If the
        merchant setting "Use Lottie animations" is on, the beat is
        played from a drop-in JSON theme asset (ss-anim-<slot>.json)
        via the self-hosted lottie_light player, lazy-loaded only after
        interaction. Otherwise the built-in SVG/CSS beat runs. Swapping
        animations = replacing the JSON asset; no code edits.
        Slots today: veg-confirm, nonveg-confirm.
     ------------------------------------------------------------------ */

  var reduceMotion = false;
  try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  var tag = document.querySelector('script[src*="diet-mode"][data-ss-lottie-player]');
  var lottieOn = !!(tag && tag.getAttribute('data-ss-lottie') === '1') && !reduceMotion;
  var lottieLoad = null;

  function loadLottie() {
    if (lottieLoad) return lottieLoad;
    lottieLoad = new Promise(function (resolve) {
      if (window.lottie) return resolve(window.lottie);
      var s = document.createElement('script');
      s.src = tag.getAttribute('data-ss-lottie-player');
      s.onload = function () { resolve(window.lottie || null); };
      s.onerror = function () { resolve(null); };
      document.head.appendChild(s);
    });
    return lottieLoad;
  }

  /* Play a slot into mount. Falls back to svgFn markup on any failure. */
  function playSlot(slot, mount, svgFn) {
    var path = tag ? tag.getAttribute('data-ss-anim-' + (slot === 'veg-confirm' ? 'veg' : 'nonveg')) : null;
    if (!lottieOn || !path) { mount.innerHTML = svgFn(); return; }
    if (!window.lottie) {
      /* player still fetching: play the built-in SVG beat now (never an
         empty or mid-swapped beat) and warm the player for the next flip */
      mount.innerHTML = svgFn();
      loadLottie();
      return;
    }
    try {
      window.lottie.loadAnimation({ container: mount, renderer: 'svg', loop: false, autoplay: true, path: path });
    } catch (e) { mount.innerHTML = svgFn(); }
  }

  function leafSVG() {
    return '<svg class="ds-confirm__leaf" viewBox="0 0 20 20" aria-hidden="true">' +
      '<path d="M10 17.5 C9.6 11 12 5.5 17 2.8 C17.4 10 14 15.2 10 17.5 Z M10 17.5 C8.4 13 6 10.4 3.2 9.2" ' +
      'fill="none" stroke="#6dc98f" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }
  function emberSVG() {
    return '<svg viewBox="0 0 20 20" aria-hidden="true"><g class="ds-confirm__ember">' +
      '<path d="M10 1.8 C12.6 5.4 16 7.6 16 11.6 A6 6 0 1 1 4 11.6 C4 7.6 7.4 5.4 10 1.8 Z" fill="#b23a2b"/>' +
      '<path d="M10 8.4 C11.4 10.2 13 11.2 13 13.2 A3 3 0 1 1 7 13.2 C7 11.2 8.6 10.2 10 8.4 Z" fill="#d9a441"/>' +
      '</g></svg>';
  }

  var confirmTimer = null;
  function showConfirm(mode, anchor) {
    try {
      var old = document.querySelector('.ds-confirm');
      if (old && old.parentNode) old.parentNode.removeChild(old);
      if (confirmTimer) { clearTimeout(confirmTimer); confirmTimer = null; }

      var chip = document.createElement('div');
      chip.className = 'ds-confirm';
      chip.setAttribute('aria-hidden', 'true');
      var mark = '<span class="ds-mark ds-mark--' + mode + '"><i></i></span>';
      /* label text reuses the toggle's own strings: no new copy */
      var label = mode === VEG ? 'VEG' : 'NON-VEG';
      chip.innerHTML = mark + '<span class="ds-confirm__txt">' + label + '</span><span class="ds-confirm__beat"></span>';
      document.body.appendChild(chip);

      /* anchor under the header toggle; centre-fallback if unplaceable */
      var r = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect() : null;
      if (r && r.width > 0) {
        chip.style.top = Math.round(r.bottom + 10) + 'px';
        var right = Math.round(window.innerWidth - r.right);
        chip.style.right = Math.max(right, 8) + 'px';
      } else {
        chip.style.top = '72px';
        chip.style.right = '12px';
      }

      var beat = chip.querySelector('.ds-confirm__beat');
      playSlot(mode === VEG ? 'veg-confirm' : 'nonveg-confirm', beat, mode === VEG ? leafSVG : emberSVG);

      /* double rAF so the entrance transition actually runs */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { chip.classList.add('ds-in'); });
      });
      confirmTimer = setTimeout(function () {
        chip.classList.add('ds-out');
        setTimeout(function () {
          if (chip.parentNode) chip.parentNode.removeChild(chip);
        }, reduceMotion ? 0 : 240);
      }, 1500);
    } catch (e) { /* decorative: must never break the page */ }
  }

  /* PDP veg chip: only where a real veg set exists (media-gallery--has-veg) */
  function ensureVegChip() {
    try {
      var gal = document.querySelector('media-gallery.media-gallery--has-veg');
      if (!gal || gal.querySelector('.ds-veg-chip')) return;
      /* anchor to the first VISIBLE veg media item: rides the image
         itself and works in both the mobile slideshow and the desktop
         grid, which keep separate DOM (critique loop 1) */
      var cands = gal.querySelectorAll("[data-diet='veg']");
      var host = null;
      for (var ci = 0; ci < cands.length; ci++) {
        if (cands[ci].offsetWidth > 0 && cands[ci].offsetHeight > 0) { host = cands[ci]; break; }
      }
      if (!host) host = cands.length ? cands[0] : gal;
      try {
        if (window.getComputedStyle(host).display === 'contents' && host.firstElementChild) {
          host = host.firstElementChild;
        }
      } catch (e) {}
      host.classList.add('ds-chip-host');
      var chip = document.createElement('span');
      chip.className = 'ds-veg-chip';
      chip.setAttribute('aria-hidden', 'true');
      chip.innerHTML = '<span class="ds-mark ds-mark--veg"><i></i></span>' +
        '<span class="ds-veg-chip__txt">100% VEG</span>';
      host.appendChild(chip);
    } catch (e) { /* decorative */ }
  }

  /* breakpoint change can hide the chip's host (slideshow <-> grid):
     re-seat it on the visible one */
  var chipResizeT = null;
  window.addEventListener('resize', function () {
    if (chipResizeT) clearTimeout(chipResizeT);
    chipResizeT = setTimeout(function () {
      chipResizeT = null;
      if (getMode() !== VEG) return;
      var c = document.querySelector('.ds-veg-chip');
      if (c && c.offsetWidth === 0 && c.parentNode) {
        c.parentNode.removeChild(c);
        ensureVegChip();
      }
    }, 250);
  });

  /* ---------------- events (delegated: survives DOM morphing) ---------------- */

  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-diet-toggle]') : null;
    if (!t) return;
    var next = getMode() === VEG ? NONVEG : VEG;
    setMode(next, 'DietModeChange');
    /* one-beat change pop on the newly-active mark (CSS keyframe) */
    t.classList.remove('ds-anim');
    void t.offsetWidth; /* restart the animation on rapid re-taps */
    t.classList.add('ds-anim');
    setTimeout(function () { t.classList.remove('ds-anim'); }, 450);
    showConfirm(next, t);
  });

  /* preload the veg set the moment a toggle interaction looks likely */
  function preload(e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-diet-toggle]') : null;
    if (t && !hydrated) hydrate();
  }
  document.addEventListener('pointerenter', preload, true);
  document.addEventListener('touchstart', preload, { capture: true, passive: true });

  /* ---------------- boot ---------------- */

  function boot() {
    apply(getMode());
    if (getMode() === VEG) hydrate();
    cleanAlts();

    /* Savor uses morph/view-transition navigation: re-hydrate + re-apply on
       swapped-in content. Debounced; all visual work stays in CSS. */
    var main = document.getElementById('MainContent');
    if (main && 'MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = window.setTimeout(function () {
          pending = null;
          apply(getMode());
          cleanAlts();
          if (hydrated || getMode() === VEG) hydrate();
        }, 150);
      }).observe(main, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
